import { type RefObject, useCallback, useEffect, useState } from "react";

interface UseMediaControllerResult {
  controller: HTMLElement | null;
  dispatchMediaEvent: (eventName: string) => void;
}

export const useMediaController = <T extends Element>({
  videoRef,
}: {
  videoRef: RefObject<T | null>;
}): UseMediaControllerResult => {
  const [controller, setController] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const mediaElement = videoRef.current;

    let resolvedController: HTMLElement | null = null;

    if (mediaElement) {
      const mediaTheme = mediaElement.closest<HTMLElement>("media-theme");
      resolvedController =
        mediaTheme?.shadowRoot?.querySelector<HTMLElement>(
          "media-controller",
        ) ?? null;
    }

    setController(resolvedController);

    return () => {
      setController(null);
    };
  }, [videoRef]);

  const dispatchMediaEvent = useCallback(
    (eventName: string) => {
      if (!controller) return;

      controller.dispatchEvent(
        new CustomEvent(eventName, { bubbles: true, composed: true }),
      );
    },
    [controller],
  );

  return { controller, dispatchMediaEvent };
};
